import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import { Message } from "semantic-ui-react";
import Link from 'next/link';

const JapanArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/beaten-track-japan`;

  return (
    <MainLayout>
      <NextSeo
        title='9 Off-The-Beaten-Track Places To Visit In Japan | DAYMADE'
        description='DAYMADE goes on a quest to discover the most authentic parts of Japan. Emerge yourself in local culture in these 8 off the beaten track locations.'
        openGraph={{
          title: '9 Off-The-Beaten-Track Places To Visit In Japan | DAYMADE',
          description: 'DAYMADE goes on a quest to discover the most authentic parts of Japan. Emerge yourself in local culture in these 8 off the beaten track locations.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/9-off-the-beaten-track-places-to-visit-in-japan',
          images: [{
            url: `${IMAGE_DIRECTORY}/hero.jpg`,
            alt: 'main with hat sat in Japanese house overlooking peaceful pond and yellow/pink cherry blossom trees'
          }]
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'instagram,instagrammable,japan,competition,tokyo,travel,destinations,holiday,prizes,getaway'
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText='main with hat sat in Japanese house overlooking peaceful pond and yellow/pink cherry blossom trees'
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            9 Off-The-Beaten-Track Places To Visit <strong>In Japan</strong>
          </h1>

          <div className='separator' style={{ width: '80%', marginTop: 10 }} />

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '2em' }}>
            <span className='bigFirstLetter'>T</span>okyo is by far Japan’s most popular tourist destination, with Kyoto coming in a close second – two stops on Japan’s Golden Route, the most common travel itinerary for oversea travellers visiting Japan. This infamous route starts by landing in Tokyo, spending a few days in Japan’s capital city, boarding a bullet train to the ancient city of Kyoto and then on to Osaka and, often, Hiroshima.
          </p>

          <p>
            ...Snooze. It’s all been done before!
          </p>

          <p>
            We at DAYMADE believe travelling isn’t about visiting the major places simply so you can cross it off your list! It’s about understanding the local lifestyle and history while immersing yourself in a different culture to open up your mind to new perspectives. Continue reading for some of our favourite destinations (there are so many!) and for inspiration to create your very own Golden Route the next time you visit the Land of the Rising Sun.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt='pink/red octopus tentacles chilling on ice before being cooked'
            className='singleBlogImage'
          />

          <h4>
            Noh Theatre
          </h4>

          <p>
            When you’re flying in from abroad, you’re most likely going to land in one of the major cities. Japanese cities offer loads of opportunities to experience the super traditional side of Japan, like an oasis within the country’s hyper-modern city life. Rather than flock to one of the many touristy destinations or overrun temples, DAYMADE suggests having a night on the town by attending a Noh Theatre performance.
          </p>

          <p>
            You may already be familiar with Noh performances, which are popular for the wooden masks carved from Japanese cypress and worn by the main character, or “Shite”. The theatre is a sight in itself to behold as stages are always built with the same simple motif: an open front and sides except for the back wall which features a painted pine tree. A bridge runs off the stage for the performers to exit and enter. All actors in Noh performances are male, and they each represent various beings. Depending on the role (there are five assigned roles altogether), these characters either portray living people, mystical beings or serve as supporting actors. This type of theatre can only be found in Japan and is definitely worth seeing!
          </p>

          <h4>
            Nara
          </h4>

          <p>
            Nara is a fantastic place to start after leaving the city. Located equally about an hour away from Osaka and Tokyo, Nara’s harmonious vibe with lots of nature is a great destination when veering off the usual route. Most people associate tea ceremonies with Kyoto, but Nara was actually the first place to perform tea ceremonies in Japan. Another unique feature is the semi-tamed wild deer walking around the city. You can even touch and feed them! So, if you’ve never got close to a deer before, especially a wild one living in the city, you can buy some feed from a local vendor and give these docile creatures – and yourself! – a real treat.
          </p>

          <h4>
            Tottori Prefecture
          </h4>

          <p>
            You’re sure to make more authentic and inspiring encounters by going off the beaten path, that’s why the next leg of our tour heads up north to Tottori. The Tottori Prefecture may be Japan’s least populated prefecture, but it has everything and beyond for the authentic Japan experience. This not very well-known prefecture is easily reachable by direct bus from most of the major cities or trains. Since it’s located along the coastline of the Sea of Japan, Tottori is the perfect place to enjoy both the sea and the mountains.
          </p>

          <p>
            You won’t be disappointed in way of culinary delights either. The famous Shabu Shabu actually originated in Tottori! You can even take a trip to Takumi restaurant, the source of where this wildly popular food was created by Shoya Yoshida. Tottori prefecture is truly a fantastic place to meet friendly locals, try the fresh cuisine, immerse yourself in serene nature and experience the historic atmosphere.
          </p>

          <h4>
            Stroll through a bamboo forest
          </h4>

          <p>
            Next stop: Taikyuji Station. Choo choo!
          </p>

          <p>
            People often go to Kyoto to see the bamboo, but you can see bamboo in its element by visiting Tottori’s countryside. Located near the former Taikyuji Station (which is no longer in operation), these abandoned train tracks are surrounded by an impressive bamboo forest. Walk along abandoned train tracks to immerse yourself in a wild bamboo forest. Being engulfed by these gigantic bamboo trees will help you tap into a Zen state of mind, especially because it’s almost guaranteed that you won’t see another tourist in sight.
          </p>

          <h4>
            Mount Mitoku
          </h4>

          <p>
            Mount Mitoku is a true hidden gem for hiking, panoramic views over the mountains and getting a glimpse of how the monks used to live. In preparation for your hike, make sure to strap on the local “waraji” sandals for the full experience this mountain has to offer. Commonly worn in the olden days, these open-toe sandals are made out of straw which provides good grip along the trails. In sandals, you can climb the sides of the steep mountainside by grabbing on to tree roots, stones and chains for the chance to train like the monks who once resided in the temple area. The trail to the top takes around 2 hours there and back. Mount Mitoku is really worth climbing and once you’ve made it to the top, you can say you’ve trained like a monk!
          </p>

          <div className='doubleImageContainer'>
            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt='tall, angular Japanese building with people looking out from top floor'
              className='doubleBlogImage'
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
              alt='glowing red Japanese lantern stands out against dimly lit blue street with bicycles and shop signs'
              className='doubleBlogImage'
            />
          </div>

          <h4>
            Uradome Coast
          </h4>

          <p>
            After you’ve breathed in enough mountain air, it’s time to hit the water! Uradome Coast stretches for approximately 15 kilometres, from Cape Kugami to Mt. Shichiyama and is popular for its clear waters. One option is to hire a boat tour, but if you want to really see what this beach is all about, it’s worth renting one of the clear kayaks. Once aboard and out on the water you can look to the bottom of the sea on a clear day while kayaking through caves, cliffs and large rock formations. It’s also a popular place for snorkelling and diving. After winding down from the day’s activities, you can really live like a local by playing Suika Wari at the beach: Suika Wari is a game where each person spins around and tries to crack open a watermelon with a baseball bat – all while blindfolded. There are only winners – everybody shares the watermelon together afterwards!
          </p>

          <h4>
            Totorri’s Sand Dunes
          </h4>

          <p>
            Not too far away from the beaches are Totorri’s infamous sand dunes. Totorri’s sand dunes are Japan’s most famous and the largest in the country. It’s best to set your alarm because it can get really hot once the sun comes up. There are loads of sandy activities to do here from sandboarding to paragliding down the dunes, all the way to practicing yoga at sunrise – all before heading over to the coast for a refreshing swim.
          </p>

          <h4>
            Kanazawa
          </h4>

          <p>
            It’s totally possible to spend an entire trip in Trotorri! But if you have more time on your hands or want to hit up as many spots as possible, then DAYMADE suggests venturing over to Kanazawa prefecture where you can...
          </p>

          <h4>
            Visit a Geisha House
          </h4>

          <p>
            Kaikaro Geisha House is a great place to discover the mystery of the Geiko, music from the 3-stringed banjo, and other-worldly dance performances. This traditional former geisha house dates back to 1820 and once inside, you are able to see what living conditions were like for the average geisha back in the day. It’s well worth the small price you pay to enter and is a learning experience where you can take great pictures and catch a glimpse into a geisha's lifestyle. Remember to save room for a matcha at the tea house!
          </p>

          <h4>
            The life of a Samurai
          </h4>

          <p>
            The Nomura Samurai House is located in the Samurai district of Kanazawa – the name of both the house and the district says it all! The Nomura Samurai House offers an excellent perspective of the contrast between the warrior nature and self-reflection of Samurai life. The enchanting garden is a tranquil spot to enjoy a traditional tea and contemplate life. After visiting the house, have a tea in the tearoom overlooking the garden and meditate on your Samurai side!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt='Japanese store front with bicycles and posters outside'
            className='singleBlogImage'
          />

          <h4>
            Japan has it all
          </h4>

          <p>
            Like a quick glimpse out of the bullet train, your trip to Japan is sure to pass by in a flash! That’s why venturing off the usual Golden Route is the best way to immerse yourself in the real Japanese experience and have meaningful encounters with the friendly locals. Sometimes you don't even have to wander far to discover something incredible. A short bus ride in either direction from a big city can land you in a place worth writing stories about.
          </p>

          <Message>
            Always wanted to explore Japan yourself?! Enter the {''}
            <Link href='/'>
              <a style={{ margin: 0, fontWeight: 'bold' }}>DAYMADE Draw</a>
            </Link>
            {''} for a chance to win an epic trip to Japan!
          </Message>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default JapanArticle;
